import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

const PUBLIC_PATHS = ["/login", "/forgot-password", "/reset-password", "/cliente/", "/condivisione/"];
const isPublicPath = () => PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

let csrfToken = null;
export const setCsrfToken = (t) => { if (t) csrfToken = t; };
let enrollToken = null;
let vistaHeader = "";
export const setVistaHeader = (v) => { vistaHeader = v || ""; };
export const setEnrollToken = (t) => { enrollToken = t || null; };

const MUTATING = ["post", "put", "patch", "delete"];
api.interceptors.request.use((config) => {
  if (MUTATING.includes((config.method || "").toLowerCase())) {
    const csrf = csrfToken || getCookie("csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  if (enrollToken) config.headers["X-Enroll-Token"] = enrollToken;
  if (vistaHeader) config.headers["X-Vista"] = vistaHeader;
  return config;
});

let refreshing = null;
api.interceptors.response.use(
  (r) => {
    const t = r?.data?.csrf_token;
    if (t) csrfToken = t;
    if ((r?.config?.url || "").includes("/auth/logout")) csrfToken = null;
    return r;
  },
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const url = original.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/2fa/");
    if (status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
        if (!isPublicPath()) window.location.href = "/login";
        return Promise.reject(e);
      }
    }
    if (status === 401 && url.includes("/auth/refresh")) {
      if (!isPublicPath()) window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export const euro = (n) => {
  const v = Math.round(Number(n) || 0);
  const grouped = Math.abs(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${v < 0 ? "-" : ""}${grouped} €`;
};

export const numIt = (n, dec = 2) => (n == null || n === "" || isNaN(Number(n)) ? String(n ?? "") : Number(n).toLocaleString("it-IT", { maximumFractionDigits: dec }));
export const pctIt = (n, dec = 2) => `${numIt(n, dec)}%`;
export const ptIt = (n) => numIt(Math.round((Number(n) || 0) * 10) / 10, 1);

const RM_TZ = { timeZone: "Europe/Rome" };
// Parse a backend timestamp. If an ISO datetime has NO timezone marker, assume UTC
// (backend stores UTC); this keeps display consistent regardless of the viewer's device.
const toDate = (d) => {
  if (d instanceof Date) return d;
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(s)) return new Date(s + "Z");
  return new Date(s);
};

export const fmtDate = (d) => {
  if (!d) return "-";
  try { return toDate(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", ...RM_TZ }); }
  catch { return d; }
};
export const fmtTime = (d) => {
  if (!d) return "";
  try { return toDate(d).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", ...RM_TZ }); }
  catch { return ""; }
};
export const fmtDateTime = (d) => {
  if (!d) return "-";
  try { return toDate(d).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", ...RM_TZ }); }
  catch { return d; }
};

export function dupDetail(e) {
  const d = e.response?.data?.detail;
  if (e.response?.status === 409 && d && typeof d === "object") return d;
  return null;
}

export function apiError(e) {
  const d = e.response?.data?.detail;
  if (d == null) return e.message || "Errore";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  if (d.message) return d.message;
  return JSON.stringify(d);
}

export default api;
